import { Trans, useLingui } from '@lingui/react/macro';
import { CloudOff, Inbox, Lock, RefreshCw, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ApiError } from '@/shared/http';
import { getDefaultPermissionList } from '@/shared/permissions';
import type { Permission } from '@/shared/screen';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Skeleton } from '@/shared/ui/skeleton';

import type { BlockSkeletonSpec } from './block';
import { formatAgo } from './time';

interface StateLayoutProps {
  readonly icon: LucideIcon;
  readonly title: ReactNode;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
  readonly compact?: boolean | undefined;
  readonly role?: 'status' | 'alert';
  readonly className?: string | undefined;
}

function StateLayout({
  icon: Icon,
  title,
  children,
  action,
  compact,
  role,
  className,
}: StateLayoutProps) {
  return (
    <div
      role={role}
      data-slot="block-state"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        compact ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex items-center justify-center rounded-xl bg-muted text-muted-foreground',
          compact ? 'size-10' : 'size-14',
        )}
      >
        <Icon className={compact ? 'size-5' : 'size-7'} />
      </span>
      <div className="flex max-w-[360px] flex-col gap-1">
        <p className="text-base font-semibold text-foreground">{title}</p>
        {children ? <div className="text-sm text-muted-foreground">{children}</div> : null}
      </div>
      {action ? (
        <div className="flex flex-wrap items-center justify-center gap-2">{action}</div>
      ) : null}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly icon?: LucideIcon;
  /** O próximo passo (U39 §9): "Limpar filtros", "Cadastrar o primeiro…". */
  readonly action?: ReactNode;
  readonly compact?: boolean;
  readonly className?: string;
}

/** Vazio com o próximo passo (U39 §9). */
export function EmptyState({
  title,
  description,
  icon = Inbox,
  action,
  compact,
  className,
}: EmptyStateProps) {
  return (
    <StateLayout
      icon={icon}
      title={title}
      action={action}
      compact={compact}
      role="status"
      className={className}
    >
      {description}
    </StateLayout>
  );
}

export interface ErrorStateProps {
  readonly error?: ApiError | undefined;
  /** Código para o suporte quando não há `ApiError` (bloco que quebrou ao desenhar). */
  readonly code?: string | undefined;
  readonly title?: ReactNode;
  readonly onRetry?: (() => void) | undefined;
  readonly retrying?: boolean;
  readonly compact?: boolean;
  readonly className?: string;
}

/** Código curto de um erro de código (não de rede): onde quebrou e o nome do erro, `render · TypeError`. */
export function crashCode(where: 'render' | 'query', error: unknown): string {
  return `${where} · ${error instanceof Error ? error.name : typeof error}`;
}

/**
 * Código para o suporte: status (ou tipo), API, código e correlação. Nunca "Erro 500" solto. Erro que
 * não veio da API (o dado quebrou ao ser montado) sai como {@link crashCode}, não `unknown · UNKNOWN`.
 */
export function errorCode(error: ApiError): string {
  if (error.kind === 'unknown' && error.api === undefined && error.code === 'UNKNOWN') return crashCode('query', error.cause);
  return [error.status ?? error.kind, error.api, error.code, error.correlationId]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');
}

/** Erro com o que falhou, "Tentar de novo" e o código para o suporte (U39 §9). */
export function ErrorState({
  error,
  code,
  title,
  onRetry,
  retrying = false,
  compact,
  className,
}: ErrorStateProps) {
  const { t } = useLingui();
  const reason =
    error?.kind === 'network'
      ? t`Sem conexão com o servidor. Confira a rede e tente de novo.`
      : error?.kind === 'timeout'
        ? t`O servidor demorou demais para responder. Tente de novo em alguns instantes.`
        : t`O serviço não respondeu como esperado. Tente de novo; se persistir, envie o código abaixo para o suporte.`;
  return (
    <StateLayout
      icon={CloudOff}
      title={title ?? t`Não foi possível carregar`}
      compact={compact}
      role="alert"
      className={className}
      action={
        onRetry ? (
          <Button variant="primary" onClick={onRetry} loading={retrying}>
            <RefreshCw aria-hidden />
            <Trans>Tentar de novo</Trans>
          </Button>
        ) : null
      }
    >
      <p>{reason}</p>
      {error || code ? (
        <p className="mt-2 inline-block rounded-md bg-muted px-2 py-1 font-mono text-xs text-foreground">
          {error ? errorCode(error) : code}
        </p>
      ) : null}
    </StateLayout>
  );
}

const CRUD_LABELS = { Read: 'read', Create: 'create', Update: 'update', Delete: 'delete' } as const;

function usePermissionLabel() {
  const { t, i18n } = useLingui();
  const catalog = getDefaultPermissionList();
  return (permission: Permission) => {
    const entry = catalog.find((item) => item.resource === permission.resource);
    const resource = entry ? i18n._(entry.resourceDisplay) : permission.resource;
    const crud = Object.hasOwn(CRUD_LABELS, permission.action)
      ? CRUD_LABELS[permission.action as keyof typeof CRUD_LABELS]
      : undefined;
    const kind =
      crud === 'read'
        ? t`leitura`
        : crud === 'create'
          ? t`criação`
          : crud === 'update'
            ? t`edição`
            : crud === 'delete'
              ? t`exclusão`
              : permission.action;
    return t`${kind} em ${resource}`;
  };
}

export interface ForbiddenStateProps {
  /** O que falta, se o front sabe; vazio quando o 403 veio da API. */
  readonly missing?: readonly Permission[];
  readonly title?: ReactNode;
  readonly compact?: boolean;
  readonly className?: string;
}

/** Aviso neutro de falta de acesso: qual permissão falta e quem pode conceder (U39 §9, 08 §4.4). */
export function ForbiddenState({ missing = [], title, compact, className }: ForbiddenStateProps) {
  const { t } = useLingui();
  const label = usePermissionLabel();
  const list = missing.map(label).join(', ');
  return (
    <StateLayout
      icon={Lock}
      title={title ?? t`Sem permissão para esta informação`}
      compact={compact}
      role="status"
      className={className}
    >
      <p>
        {missing.length > 0
          ? t`Falta a permissão de ${list}. Quem pode conceder é o administrador do seu correspondente.`
          : t`O servidor não liberou esta informação para o seu usuário. Quem pode conceder é o administrador do seu correspondente.`}
      </p>
    </StateLayout>
  );
}

export interface StaleNoticeProps {
  readonly updatedAt: number | undefined;
  readonly now: number;
  readonly onRetry?: (() => void) | undefined;
  readonly retrying?: boolean;
  readonly error?: ApiError | undefined;
}

/** Dado desatualizado: continua na tela, com o aviso e "Tentar de novo". */
export function StaleNotice({
  updatedAt,
  now,
  onRetry,
  retrying = false,
  error,
}: StaleNoticeProps) {
  const { t, i18n } = useLingui();
  const ago = updatedAt === undefined ? '' : formatAgo(updatedAt, now, i18n.locale);
  return (
    <Alert tone="warn" role="status" title={t`Não foi possível atualizar`}>
      <p>{t`Mostrando os dados carregados ${ago}.`}</p>
      {error ? <p className="mt-1 font-mono text-xs break-words">{errorCode(error)}</p> : null}
      {onRetry ? (
        <Button size="sm" variant="secondary" className="mt-2" onClick={onRetry} loading={retrying}>
          <Trans>Tentar de novo</Trans>
        </Button>
      ) : null}
    </Alert>
  );
}

export interface BlockSkeletonProps extends BlockSkeletonSpec {
  readonly className?: string;
}

/** Skeleton com a forma final (U39 §8): tabela, lista, KPI, registro ou gráfico. */
export function BlockSkeleton({ kind, rows, columns, className }: BlockSkeletonProps) {
  const lines = Array.from({ length: rows ?? (kind === 'list' ? 4 : 5) }, (_, index) => index);
  const cols = Array.from({ length: columns ?? 4 }, (_, index) => index);
  return (
    <div
      aria-hidden
      data-slot="block-skeleton"
      data-kind={kind}
      className={cn('w-full', className)}
    >
      {kind === 'table' ? (
        <div className="flex flex-col">
          <div className="flex flex-col @min-[361px]/block:hidden">
            {lines.map((line) => (
              <div
                key={line}
                className="flex min-h-12 items-center gap-3 border-b border-border py-2.5 last:border-b-0"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <Skeleton className="h-3.5 w-3/5" />
                  <Skeleton className="h-3 w-2/5" />
                </div>
                <Skeleton className="h-3.5 w-20" />
              </div>
            ))}
          </div>
          <div className="hidden flex-col @min-[361px]/block:flex">
            <div className="flex h-9 items-center gap-4 border-b border-border">
              {cols.map((col) => (
                <Skeleton key={col} className="h-3 flex-1" />
              ))}
            </div>
            {lines.map((line) => (
              <div
                key={line}
                className="flex h-row items-center gap-4 border-b border-border last:border-b-0"
              >
                {cols.map((col) => (
                  <Skeleton
                    key={col}
                    className={cn('h-3 flex-1', col === 0 ? 'max-w-[40%]' : null)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : kind === 'list' ? (
        <div className="flex flex-col">
          {lines.map((line) => (
            <div
              key={line}
              className="flex min-h-16 items-center gap-3 border-b border-border py-3 last:border-b-0"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-3 w-2/5" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      ) : kind === 'kpi' ? (
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${String(Math.max(1, columns ?? 1))}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: Math.max(1, columns ?? 1) }, (_, index) => (
            <div key={index} className="flex flex-col gap-3 py-1">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-40 max-w-full" />
              <Skeleton className="h-3 w-32 max-w-full" />
            </div>
          ))}
        </div>
      ) : kind === 'record' ? (
        <div className="grid gap-4 @min-[361px]/block:grid-cols-2">
          {lines.map((line) => (
            <div key={line} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      ) : columns ? (
        <div className="flex h-48 items-end gap-3 border-b border-border px-2">
          {cols.map((col) => (
            <Skeleton
              key={col}
              className="w-full max-w-6 rounded-t-sm rounded-b-none"
              style={{ height: `${String(35 + ((col * 37) % 55))}%` }}
            />
          ))}
        </div>
      ) : (
        <Skeleton className="h-40 w-full rounded-lg" />
      )}
    </div>
  );
}

export interface ScreenSkeletonProps {
  /**
   * Lista (cabeçalho, filtros e tabela), detalhe (cabeçalho com status, abas e seção) ou fluxo guiado (trilho
   * vertical, etapa e "Suas escolhas", U53).
   */
  readonly archetype: 'list' | 'detail' | 'flow';
  /** Quantas abas o detalhe tem, para a faixa não mudar de largura quando o conteúdo chega. */
  readonly tabs?: number | undefined;
  readonly className?: string;
}

/**
 * Tela ainda chegando (U39 §8): a mesma anatomia do `PageHeader`/`ListScreen` e do
 * `DetailHeader`/`RecordTabs`, para a troca pelo conteúdo real não pular.
 */
export function ScreenSkeleton({ archetype, tabs = 5, className }: ScreenSkeletonProps) {
  const header = (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {archetype === 'list' ? <Skeleton className="h-3 w-28" /> : null}
        <Skeleton className="h-8 w-72 max-w-full" />
        <Skeleton className="h-3.5 w-44" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-control w-28" />
        <Skeleton className="h-control w-36" />
      </div>
    </div>
  );
  if (archetype === 'flow')
    return (
      <div aria-hidden data-slot="screen-skeleton" data-archetype="flow" className={cn('flex min-w-0 flex-col gap-5', className)}>
        <div className="flex min-w-0 flex-col gap-2">
          <Skeleton className="h-8 w-72 max-w-full" />
          <Skeleton className="h-3.5 w-56 max-w-full" />
        </div>
        {/* A grade do `FlowFrame`: trilho, etapa e, a partir de 1280 px, o painel. */}
        <div className="grid min-w-0 items-start gap-6 md:grid-cols-[12.5rem_minmax(0,1fr)] xl:grid-cols-[12.5rem_minmax(0,1fr)_17.5rem]">
          <div className="hidden flex-col gap-3 p-2 md:flex">
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className="flex items-center gap-2.5">
                <Skeleton className="size-7 shrink-0 rounded-full" />
                <Skeleton className="h-3.5 w-24" />
              </div>
            ))}
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-6 w-56 max-w-full" />
            <Skeleton className="h-3.5 w-80 max-w-full" />
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,14.5rem),1fr))]">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-20 rounded-card" />
              ))}
            </div>
          </div>
          <div className="hidden rounded-card border border-border bg-card p-4 xl:block">
            <BlockSkeleton kind="record" rows={4} />
          </div>
        </div>
      </div>
    );
  return (
    <div
      aria-hidden
      data-slot="screen-skeleton"
      data-archetype={archetype}
      className={cn('@container/block flex min-w-0 flex-col gap-5', className)}
    >
      {header}
      {archetype === 'list' ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-control w-72 max-w-full" />
            <Skeleton className="h-control w-24" />
          </div>
          <div className="rounded-card border border-border bg-card p-4">
            <BlockSkeleton kind="table" rows={6} columns={5} />
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-2 overflow-hidden border-b border-border pb-2">
            {Array.from({ length: tabs }, (_, index) => (
              <Skeleton key={index} className="h-7 w-24 shrink-0" />
            ))}
          </div>
          <div className="rounded-card border border-border bg-card p-4">
            <BlockSkeleton kind="record" rows={6} />
          </div>
        </>
      )}
    </div>
  );
}
